/**
 * context/ConfigContext
 * ──────────────────────────────────────────────────────────────────────────
 * The workspace's configuration, loaded once and shared by every screen:
 * which console this user gets, which gates are unmet, the policy, the rules,
 * the option lists and the categories.
 *
 * All of it arrives from ONE call — GET /v1/config/bootstrap. Screens read from
 * here rather than fetching their own copies, which is why:
 *   - the modal chip rows always offer the same option values the register
 *     filters by,
 *   - every screen agrees on what "stale" means (`rules.stale_days`),
 *   - and a gate bar cannot appear on one screen while another shows data it
 *     should not have.
 *
 * `reload` is called after any Settings write, because a policy save SEEDS
 * approval rules and can satisfy a gate — so the rest of the app must be told.
 * ──────────────────────────────────────────────────────────────────────────
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { fetchBootstrap } from '../services/config/config.service';
import {
  groupOptions,
  type Bootstrap,
  type Category,
  type ConsoleRole,
  type GateStateResponse,
  type ListKind,
  type OptionRow,
  type Policy,
  type Rules,
} from '../schemas/config';
import { handleApiError } from '../utils/errorHandler';
import { useSession } from './SessionContext';

export interface ConfigContextValue {
  loading: boolean;
  /** True once a successful bootstrap has landed. */
  ready: boolean;
  console: ConsoleRole;
  gates: GateStateResponse;
  policy: Policy | null;
  rules: Rules | null;
  /** Flat, as the API sends it. */
  options: OptionRow[];
  /** Grouped by list kind — what every chip row and picker actually wants. */
  optionsByKind: Record<ListKind, OptionRow[]>;
  categories: Category[];
  /** Active categories only — the spend form must not offer a dead one. */
  activeCategories: Category[];
  reload: () => Promise<void>;
}

/** Until the first bootstrap lands, every gate is treated as UNMET. */
const CLOSED_GATES: GateStateResponse = {
  console: false,
  categories: false,
  variance_set: false,
  connectors: false,
  offboard_feed: false,
};

const ConfigContext = createContext<ConfigContextValue | null>(null);

export function ConfigProvider({ children }: { children: ReactNode }) {
  const { status } = useSession();
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setBootstrap(await fetchBootstrap());
    } catch (error) {
      // The shell cannot render without this, so the failure IS user-facing.
      handleApiError(error, 'Could not load your workspace');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Wait for a session: bootstrap is an authenticated call, and firing it
    // during the probe would produce a guaranteed 401 and a spurious refresh.
    if (status === 'signed-in') void reload();
    if (status === 'signed-out') setBootstrap(null);
  }, [status, reload]);

  const value = useMemo<ConfigContextValue>(() => {
    const options = bootstrap?.options ?? [];
    const categories = bootstrap?.categories ?? [];
    return {
      loading,
      ready: bootstrap !== null,
      // Default to the narrowest console until the API says otherwise: showing
      // Finance to someone who turns out to be a custodian is worse than the
      // reverse.
      console: bootstrap?.console ?? 'custodian',
      gates: bootstrap?.gates ?? CLOSED_GATES,
      policy: bootstrap?.policy ?? null,
      rules: bootstrap?.rules ?? null,
      options,
      optionsByKind: groupOptions(options),
      categories,
      activeCategories: categories.filter((category) => category.is_active),
      reload,
    };
  }, [bootstrap, loading, reload]);

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useConfig(): ConfigContextValue {
  const value = useContext(ConfigContext);
  if (!value) throw new Error('useConfig must be used inside <ConfigProvider>');
  return value;
}
